// FeatureDetails.jsx
import React, { useState, useEffect, useRef } from "react";
import { Button } from "../ui/Button";
import { ChevronRight, XIcon, ChevronDown, ChevronUp, Search } from "lucide-react";
import { INPUT_STYLES } from '../../Constants/Const';

// ── Mobile FAQ Tab Component ──────────────────────────────────────────────────
const FAQItem = ({ faq, color, isOpen, onToggle }) => {
  return (
    <div className={`border rounded-lg overflow-hidden transition-border shadow-sm duration-200 ${isOpen ? "border-gray-200" : "border-white"}`}>
      <div
        className={`w-full flex items-center justify-between px-4 py-3 text-left ${isOpen ? "bg-white" : "bg-white"}`}
        onClick={onToggle}
      >
        <span className={`text-[14px] font-semibold  pr-3 ${isOpen ? "text-primary" : "text-[#333]"}`}>{faq.q}</span>
        {isOpen
          ? <ChevronUp size={16} className="shrink-0 text-gray-400" />
          : <ChevronDown size={16} className="shrink-0 text-gray-400" />}
      </div>
      {isOpen && (
        <div
          className="px-4 pb-3 pt-3 bg-white text-[14px] text-[#333] leading-relaxed border-t border-gray-200"
          dangerouslySetInnerHTML={{ __html: faq.a }}
        />
      )}
    </div>
  );
};

const MobileFAQPanel = ({ faqs, color }) => {
  const [search, setSearch] = useState("");
  const [openIndex, setOpenIndex] = useState(null);
  useEffect(() => setOpenIndex(null), [search]);
  const showSearch = faqs.length > 5;

  const filtered = search.trim()
    ? faqs.filter(
        (f) =>
          f.q.toLowerCase().includes(search.toLowerCase()) ||
          f.a.toLowerCase().includes(search.toLowerCase())
      )
    : faqs;

  return (
    <div className="flex flex-col gap-3 pb-24">
    {showSearch && (
      <div className="relative mb-1">
        <div className="group flex w-full border border-border rounded-lg overflow-hidden bg-white transition-all duration-300 focus-within:border-primary focus-within:ring-1 focus-within:ring-primary/20">
          
          <input
            type="text"
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border-none outline-none focus:outline-none focus:ring-0 text-sm bg-transparent"
          />

          <Search
            size={15}
            className="absolute left-3 top-3 text-gray-400 group-focus-within:text-primary"
          />

          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-3 top-2.5 text-gray-300 hover:text-gray-500"
            >
              <XIcon size={14} />
            </button>
          )}

        </div>
      </div>
    )}
      {filtered.length > 0
      ? filtered.map((faq, i) => <FAQItem key={i} faq={faq} color={color} isOpen={openIndex === i} onToggle={() => setOpenIndex(openIndex === i ? null : i)} />)
        : <p className="text-center text-gray-400 text-sm py-8">No results found.</p>}
    </div>
  );
};

// ── Mobile View ───────────────────────────────────────────────────────────────
const MobileFeatureDetails = ({ feature, onClose, onBuyNow }) => {
  const [tab, setTab] = useState("preview");
  const [currentIndex, setCurrentIndex] = useState(0);
  const intervalRef = useRef(null);
  const slowModeRef = useRef(false);

  const screens = feature?.screens || [];
  const items = feature?.items || [];
  const faqs = feature?.faqs || [];
  const hasFAQs = faqs.length > 0;

  const startAutoSlide = (delay = 2500) => {
    clearInterval(intervalRef.current);
    if (!screens.length) return;
    intervalRef.current = setInterval(() => {
      setCurrentIndex((p) => (p === screens.length - 1 ? 0 : p + 1));
    }, delay);
  };

  useEffect(() => {
    setCurrentIndex(0);
    slowModeRef.current = false;
    setTab("preview");
  }, [feature]);

  useEffect(() => {
    if (tab !== "preview" || !screens.length) {
      clearInterval(intervalRef.current);
      return;
    }
    startAutoSlide(2500);
    return () => clearInterval(intervalRef.current);
  }, [tab, screens.length]);

  const navigate = (dir) => {
    clearInterval(intervalRef.current);
    setCurrentIndex((p) => {
      const t = screens.length;
      return dir === "next" ? (p === t - 1 ? 0 : p + 1) : (p === 0 ? t - 1 : p - 1);
    });
    setTimeout(() => startAutoSlide(slowModeRef.current ? 2500 : 2500), 2500);
  };

  const handleTickClick = (idx) => {
    clearInterval(intervalRef.current);
    slowModeRef.current = true;
    setCurrentIndex(idx);
    setTimeout(() => startAutoSlide(2500), 2500);
  };

  return (
    <div className="bg-black/60 w-full h-screen flex justify-center items-center">
      <div className="bg-[#f5f5f5] w-[93%] h-[90%] flex flex-col overflow-hidden rounded-[10px]">
        {/* Header */}
        <div className="relative px-4 py-3 border-b border-gray-200 bg-white shrink-0">
          <div className="pr-10">
            <h2 className="text-xl font-bold text-[#333]  ">{feature.title}</h2>
            <p className="text-sm text-[#555] font-medium mt-0.5">{feature.tagline}</p>
          </div>
          <button
            onClick={onClose}
            className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs — only if FAQs exist */}
        {hasFAQs && (
          <div className="flex border-b border-gray-200 bg-white shrink-0">
            {["preview", "faqs"].map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`flex-1 py-2.5 text-sm font-semibold transition-all capitalize border-b-2 ${
                  tab === t
                    ? "border-primary text-primary"
                    : "border-transparent text-gray-400 hover:text-gray-600"
                }`}
              >
                {t === "preview" ? "Preview" : "FAQs"}
              </button>
            ))}
          </div>
        )}

        {/* Body — scrollable */}
        <div className="flex-1 overflow-y-auto">
          {tab === "preview" ? (
            <div className="flex flex-col h-full">
              {/* Tick list */}
              <ul className="px-4 pt-4 space-y-1.5">
                {items.map((item, idx) => {
                  const screenIdx = Math.min(idx, screens.length - 1);
                  const isActive = currentIndex === screenIdx;
                  return (
                    <li
                      key={idx}
                      onClick={() => handleTickClick(screenIdx)}
                      className={`flex items-start gap-3 text-[14px] rounded-md px-2 py-2 cursor-pointer transition-all duration-200 select-none
                        ${isActive ? "bg-white shadow-sm border border-gray-300 text-[#222]" : "border border-[#f5f5f5] text-[#444] "}`}
                    >
                      <span className="text-primary font-bold text-md leading-none shrink-0 mt-1">✓</span>
                      <span dangerouslySetInnerHTML={{ __html: item }} />
                    </li>
                  );
                })}
              </ul>

              {/* Carousel */}
              {screens.length > 0 && (
                <div className="mx-4 mt-4 mb-4 bg-white rounded-lg p-3 flex flex-col">
                  <p className="text-center font-bold text-[#333] mb-3 text-sm px-6 shrink-0">
                    {screens[currentIndex].sTitle}
                  </p>
                  <div className="flex items-center justify-between gap-1">
                    <button onClick={() => navigate("prev")} className="text-primary opacity-70 hover:opacity-100 p-1 shrink-0">
                      <ChevronRight className="w-5 h-5 rotate-180" />
                    </button>
                    <div
                      className="flex-1 flex items-center justify-center cursor-zoom-in"
                      onClick={() => {
                        const w = window.open("", "_blank");
                        w.document.write(`<html><body style="margin:0;background:#000;display:flex;align-items:center;justify-content:center;min-height:100vh"><img src="${screens[currentIndex].sPic}" style="max-width:100%;max-height:100vh;object-fit:contain"></body></html>`);
                        w.document.close();
                      }}
                    >
                      <img
                        src={screens[currentIndex].sPic}
                        alt={screens[currentIndex].sTitle}
                        className="w-full h-auto max-h-[240px] object-contain rounded"
                      />
                    </div>
                    <button onClick={() => navigate("next")} className="text-primary opacity-70 hover:opacity-100 p-1 shrink-0">
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="flex justify-center gap-2 mt-3">
                    {screens.map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleTickClick(idx)}
                        className={`w-1.5 h-1.5 rounded-full transition-colors ${idx === currentIndex ? "bg-primary" : "bg-gray-300"}`}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="px-4 pt-4">
              <MobileFAQPanel faqs={faqs} color="var(--color-primary)" />
            </div>
          )}
        </div>

        {/* Mobile CTA */}
        <div className="shrink-0 p-4 bg-[#f5f5f5] border-t border-gray-100">
          <Button
            onClick={onBuyNow}
            size="sm"
            className="w-full bg-primary text-white border-none px-8 py-3 rounded-md font-bold text-lg"
          >
            Buy Now
          </Button>
        </div>
      </div>
    </div>
  );
};

// ── Desktop View (unchanged logic) ───────────────────────────────────────────
const DesktopFeatureDetails = ({ feature, onClose, onBuyNow }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const intervalRef = useRef(null);
  const slowModeRef = useRef(false);

  const screens = feature?.screens || [];
  const items = feature?.items || [];

  const startAutoSlide = (delay = 2500) => {
    clearInterval(intervalRef.current);
    if (!screens.length) return;
    intervalRef.current = setInterval(() => {
      setCurrentIndex((p) => (p === screens.length - 1 ? 0 : p + 1));
    }, delay);
  };

  useEffect(() => {
    setCurrentIndex(0);
    slowModeRef.current = false;
  }, [feature]);

  useEffect(() => {
    if (!screens.length) return;
    startAutoSlide(2500);
    return () => clearInterval(intervalRef.current);
  }, [screens.length]);

  const navigate = (dir) => {
    clearInterval(intervalRef.current);
    setCurrentIndex((p) => {
      const t = screens.length;
      return dir === "next" ? (p === t - 1 ? 0 : p + 1) : (p === 0 ? t - 1 : p - 1);
    });
    setTimeout(() => startAutoSlide(slowModeRef.current ? 2500 : 2500), 2500);
  };

  const handleTickClick = (idx) => {
    clearInterval(intervalRef.current);
    slowModeRef.current = true;
    setCurrentIndex(idx);
    setTimeout(() => startAutoSlide(2500), 2500);
  };

  return (
    <div
      className="bg-[#f5f5f5] w-full max-w-6xl rounded-lg shadow-2xl md:h-auto overflow-hidden relative animate-in fade-in zoom-in duration-300 my-auto"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="relative px-5 py-4">
        <div className="pr-12">
          <h2 className="text-3xl font-bold text-[#333]  ">{feature.title}</h2>
          <p className="text-lg text-[#555] font-medium mt-0.5">{feature.tagline}</p>
        </div>
        <button onClick={onClose} className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-200 transition-colors">
          <XIcon className="w-5 h-5" />
        </button>
      </div>

      {/* Body */}
      <div className="px-6 py-6 pt-5 flex flex-row gap-0">
        {/* Left */}
        <div className="w-1/3 flex flex-col justify-between mr-4">
          <ul className="space-y-2">
            {items.map((item, idx) => {
              const screenIdx = Math.min(idx, screens.length - 1);
              const isActive = currentIndex === screenIdx;
              return (
                <li
                  key={idx}
                  onClick={() => handleTickClick(screenIdx)}
                  className={`flex items-center gap-3 text-[15px] rounded-md px-2 py-2 cursor-pointer transition-all duration-300 select-none
                    ${isActive ? "bg-white shadow-sm text-[#222] border border-gray-300" : "text-[#444] border border-[#f5f5f5] hover:bg-white/60"}`}
                >
                  <span className="text-primary font-bold text-lg mt-[-2px] shrink-0">✓</span>
                  <span dangerouslySetInnerHTML={{ __html: item }} />
                </li>
              );
            })}
          </ul>
          <div className="mt-8 flex gap-3">
            <Button onClick={onBuyNow} size="md" className="bg-primary text-white border-none px-8 py-3 rounded-md font-bold text-lg">Buy Now</Button>
            <Button onClick={onClose} size="md" variant="outline" className="px-7 py-2 rounded-md font-bold text-lg">Close</Button>
          </div>
        </div>

        {/* Right: Carousel */}
        <div className="w-2/3 bg-white rounded-lg relative flex flex-col min-h-[480px] p-4">
          {screens.length > 0 ? (
            <>
              <p className="text-center font-bold text-[#333] mb-3 text-base px-8 shrink-0">
                {screens[currentIndex].sTitle}
              </p>
              <div className="flex-1 flex items-center justify-between gap-1 min-h-0">
                <button onClick={() => navigate("prev")} className="shrink-0 text-primary hover:scale-110 transition-transform opacity-70 hover:opacity-100 p-1">
                  <ChevronRight className="w-7 h-7 rotate-180" />
                </button>
                <div
                  className="flex-1 min-w-0 flex items-center justify-center cursor-zoom-in"
                  onClick={() => {
                    const w = window.open("", "_blank");
                    w.document.write(`<html><body style="margin:0;background:#000;display:flex;align-items:center;justify-content:center;min-height:100vh"><img src="${screens[currentIndex].sPic}" style="max-width:100%;max-height:100vh;object-fit:contain"></body></html>`);
                    w.document.close();
                  }}
                >
                  <img src={screens[currentIndex].sPic} alt={screens[currentIndex].sTitle} className="w-full h-auto max-h-[380px] object-contain rounded transition-opacity duration-300" />
                </div>
                <button onClick={() => navigate("next")} className="shrink-0 text-primary hover:scale-110 transition-transform opacity-70 hover:opacity-100 p-1">
                  <ChevronRight className="w-7 h-7" />
                </button>
              </div>
              <div className="shrink-0 flex justify-center gap-2 mt-3">
                {screens.map((_, idx) => (
                  <button key={idx} onClick={() => handleTickClick(idx)} className={`w-2 h-2 rounded-full transition-colors ${idx === currentIndex ? "bg-primary" : "bg-gray-300"}`} />
                ))}
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-full text-gray-400 italic">No screens available</div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Main Export ───────────────────────────────────────────────────────────────
const FeatureDetails = ({ isOpen, onClose, feature, onBuyNow }) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // Back button + scroll lock
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      window.history.pushState({ modal: "feature" }, "");
      const handlePopState = () => onClose();
      window.addEventListener("popstate", handlePopState);
      return () => {
        window.removeEventListener("popstate", handlePopState);
        document.body.style.overflow = "";
        if (window.history.state?.modal === "feature") window.history.back();
      };
    }
  }, [isOpen, onClose]);

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === "Escape") onClose(); };
    if (isOpen) window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [isOpen, onClose]);

  if (!isOpen || !feature) return null;

  if (isMobile) {
    return (
      <div className="fixed inset-0 z-[100]">
        <MobileFeatureDetails feature={feature} onClose={onClose} onBuyNow={onBuyNow} />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center p-4 bg-black/60 overflow-y-auto" onClick={onClose}>
      <DesktopFeatureDetails feature={feature} onClose={onClose} onBuyNow={onBuyNow} />
    </div>
  );
};

export default FeatureDetails;